import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { CheckCircle2, Clock, AlertTriangle, Info, XCircle, User, Mail } from 'lucide-react';
import { Alert } from '@/types/shelter';
import { supabase } from '@/integrations/supabase/client';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { logActivity } from '@/hooks/useActivityLog';

interface AlertDetailDialogProps {
  alert: Alert | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  acknowledgerName?: string;
  onAlertResolved?: (alertTitle: string) => void;
}

const AUTO_RESOLVE_PATTERNS = ['has mail', 'has package', 'has mail & packages'];
const LEGACY_APPOINTMENT_FALLBACK = 'No appointment details were captured for this reminder. Open the client profile to update or replace it with a detailed reminder.';

function shouldAutoResolve(title: string): boolean {
  return AUTO_RESOLVE_PATTERNS.some(p => title.toLowerCase().includes(p));
}

function transformMailTitle(title: string): string {
  return title
    .replace('has mail & packages', 'received mail & packages')
    .replace('has mail', 'received mail')
    .replace('has package', 'received package');
}

export default function AlertDetailDialog({ alert, open, onOpenChange, acknowledgerName, onAlertResolved }: AlertDetailDialogProps) {
  const [loading, setLoading] = useState(false);
  const queryClient = useQueryClient();

  if (!alert) return null;

  const isMockAlert = alert.id.startsWith('client-');
  const isResolved = alert.status === 'resolved';
  const isMailAlert = shouldAutoResolve(alert.title);
  const isAppointmentReminder = alert.title.toLowerCase().startsWith('appointment reminder');
  const appointmentDetail = isAppointmentReminder
    ? alert.title.replace(/^appointment reminder:?\s*/i, '').trim()
    : '';
  const displayDescription = isAppointmentReminder && !appointmentDetail && !alert.description?.includes('Due:')
    ? LEGACY_APPOINTMENT_FALLBACK
    : alert.description;

  const handleResolve = async () => {
    if (!alert) return;
    
    // Mock alerts can't be resolved via DB
    if (isMockAlert) {
      toast.info('This alert needs to be resolved from the client profile');
      return;
    }
    
    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const updateData: any = { 
        status: 'resolved', 
        acknowledged_by: user.id 
      };
      if (isMailAlert) {
        updateData.title = transformMailTitle(alert.title);
      }
      await supabase.from('alerts').update(updateData).eq('id', alert.id);

      // Log activity so it appears in Today's Activity
      await logActivity({
        actionType: 'acknowledge_alert',
        actionDetails: {
          alertId: alert.id,
          alertTitle: alert.title,
          resolvedTitle: isMailAlert ? transformMailTitle(alert.title) : alert.title,
          clientId: alert.clientId || null,
          timestamp: new Date().toISOString()
        }
      });

      toast.success(isMailAlert ? 'Mail received' : 'Alert resolved');
      queryClient.invalidateQueries({ queryKey: ['alerts'] });
      onAlertResolved?.(alert.title);
      onOpenChange(false);
    } catch (e: any) {
      toast.error(e.message || 'Failed to resolve alert');
    } finally {
      setLoading(false);
    }
  };

  const getTypeIcon = () => {
    const wrap = (child: React.ReactNode) => (
      <span className="relative inline-flex h-9 w-9 items-center justify-center rounded-full alert-glass-icon-halo">
        {child}
      </span>
    );
    switch (alert.type) {
      case 'critical': return wrap(<XCircle className="h-5 w-5" style={{ color: 'hsl(var(--alert-ruby))' }} />);
      case 'warning': return wrap(<AlertTriangle className="h-5 w-5 text-yellow-600" />);
      default: return wrap(<Info className="h-5 w-5 text-blue-600" />);
    }
  };

  const getStatusBadge = () => {
    if (isResolved) return <Badge className="bg-green-100 text-green-800 border-green-200 text-xs">Resolved</Badge>;
    return <Badge className="alert-glass-badge text-xs font-medium">Active</Badge>;
  };

  const getCategoryBadge = () => {
    const colors: Record<string, string> = {
      client: 'bg-purple-100 text-purple-800 border-purple-200',
      staff: 'bg-blue-100 text-blue-800 border-blue-200',
      service: 'bg-green-100 text-green-800 border-green-200',
      building: 'bg-orange-100 text-orange-800 border-orange-200',
      system: 'bg-red-100 text-red-800 border-red-200',
    };
    const color = colors[alert.category || 'system'] || colors.system;
    return <Badge className={`text-xs border ${color}`}>{(alert.category || 'system').toUpperCase()}</Badge>;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md alert-glass-surface border-0">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3 text-base">
            {isMailAlert ? (
              <span className="relative inline-flex h-9 w-9 items-center justify-center rounded-full alert-glass-icon-halo">
                <Mail className="h-5 w-5 text-orange-500" />
              </span>
            ) : getTypeIcon()}
            Alert Details
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Status & Category */}
          <div className="flex items-center gap-2">
            {getStatusBadge()}
            {getCategoryBadge()}
            <Badge variant="outline" className="text-xs capitalize">{alert.type}</Badge>
          </div>

          {/* Title & Description */}
          <div>
            <h3 className="font-semibold text-sm">{alert.title}</h3>
            {isAppointmentReminder && appointmentDetail && (
              <p className="text-sm mt-1 font-medium">{appointmentDetail}</p>
            )}
            <p className="text-sm text-muted-foreground mt-1 whitespace-pre-line">{displayDescription}</p>
          </div>

          <Separator />

          {/* Metadata */}
          <div className="space-y-2 text-sm">
            <div className="flex items-center gap-2 text-muted-foreground">
              <Clock className="h-3.5 w-3.5" />
              Created: {format(new Date(alert.timestamp), 'MMM d, yyyy h:mm a')}
            </div>
            {alert.acknowledgements && alert.acknowledgements.length > 0 && (
              <div className="flex items-center gap-2 text-muted-foreground">
                <User className="h-3.5 w-3.5" />
                Acknowledged by {alert.acknowledgements.length} staff
              </div>
            )}
          </div>

          {/* Actions */}
          {!isResolved && (
            <>
              <Separator />
              <div className="flex justify-end gap-2">
                {isMockAlert ? (
                  <p className="text-xs text-muted-foreground italic">
                    Resolve this from the client's profile or alerts section.
                  </p>
                ) : (
                  <Button size="sm" onClick={handleResolve} disabled={loading}>
                    <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" />
                    {isMailAlert ? 'Received' : 'Resolve'}
                  </Button>
                )}
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
